import React from "react";
import {Button, notification, Upload, type MenuProps} from "antd";
import {SaveOutlined, UploadOutlined, UserOutlined} from "@ant-design/icons";
import type {ColDef} from "ag-grid-community";
import type {IntlShape} from "react-intl";
import * as Types from "../../../../../__generated__/types";
import {
    getSelfEntitlementResultByFilter_getSelfEntitlementResultByFilter_partners,
} from "../../../../../main/__generated__/getSelfEntitlementResultByFilter";
import {getI18n, isUnauthorizedError} from "../../../../../utils/Utils";
import { ADDITIONAL_FREIGHT_TABS_ENTITLEMENT } from "../freightEntitlements";

export interface BeleganforderungFilter {
    partnerNo?: any;
    borderoNumber?: string;
    dateFrom?: any;
    dateUntil?: any;
    status?: Types.BorderoEdpStatus[];
}

export interface BeleganforderungRow {
    carrierNumber: string;
    borderoNumber: string;
    borderoDate: string;
    borderoPosition: string;
    orderArea?: string;
    relation?: string;
    goodsCategory?: string;
    loadNumber?: string;
    consignee?: string;
    consignor?: string;
    volume?: number;
    weight?: number;
    status?: string;
}

export interface BeleganforderungProps {
    currentUser?: any;
    distinctEntitlements?: string[];
}

interface UploadCellRendererProps {
    data?: BeleganforderungRow;
    onFileSelected: (file: File, row: BeleganforderungRow) => Promise<void>;
    uploadLabel: string;
    uploadingRowKey: string | null;
}

export const FreightEntitlement = ADDITIONAL_FREIGHT_TABS_ENTITLEMENT;

export const onlyShowFreightPartners = (
    data: getSelfEntitlementResultByFilter_getSelfEntitlementResultByFilter_partners[],
) => Array.from(data.filter((item) => item?.category === "Z").values());

export function createRowKey(row: BeleganforderungRow): string {
    return [
        row.carrierNumber,
        row.borderoNumber,
        row.borderoDate,
        row.borderoPosition,
    ].join("::");
}

function normalizeNumber(input: string, stripLeadingZeros: boolean): string {
    const normalizedInput = input.trim();

    if (/^\d+(\.0+)?$/.test(normalizedInput)) {
        const integerPart = normalizedInput.split(".")[0] || "0";
        if (!stripLeadingZeros) {
            return integerPart;
        }

        const normalized = integerPart.replace(/^0+/, "");
        return normalized || "0";
    }

    if (!stripLeadingZeros) {
        return normalizedInput;
    }

    const normalized = normalizedInput.replace(/^0+/, "");
    return normalized || "0";
}

function buildNormalizedBulkUploadKey(partnerNumber: string, borderoNumber: string, positionNumber: string): string {
    return [normalizeNumber(partnerNumber, false), borderoNumber.trim(), normalizeNumber(positionNumber, true)]
        .join("|")
        .toLowerCase();
}

export function buildBulkUploadFileKey(row: BeleganforderungRow): string {
    return buildNormalizedBulkUploadKey(row.carrierNumber, row.borderoNumber, row.borderoPosition);
}

export function getFileNameWithoutExtension(fileName: string): string {
    const extensionIndex = fileName.lastIndexOf(".");
    return extensionIndex > 0 ? fileName.substring(0, extensionIndex) : fileName;
}

export interface BulkUploadFileMetadata {
    carrierNumber: string;
    borderoNumber: string;
    borderoPosition: string;
}

export function parseBulkUploadFileMetadataFromName(fileName: string): BulkUploadFileMetadata | null {
    let normalizedName = getFileNameWithoutExtension(fileName)
        .trim()
        .replaceAll("-", "+")
        .replaceAll("_", "+");

    while (normalizedName.includes("++")) {
        normalizedName = normalizedName.replaceAll("++", "+");
    }

    const segments = normalizedName.split("+").filter(Boolean);

    if (segments.length !== 3) {
        return null;
    }

    const [partnerNumber, borderoNumber, positionNumber] = segments;
    const normalizedCarrierNumber = normalizeNumber(partnerNumber, false);
    const normalizedBorderoNumber = borderoNumber.trim();
    const normalizedPosition = normalizeNumber(positionNumber, true);

    if (!normalizedCarrierNumber || !normalizedBorderoNumber || !normalizedPosition) {
        return null;
    }

    return {
        carrierNumber: normalizedCarrierNumber,
        borderoNumber: normalizedBorderoNumber,
        borderoPosition: normalizedPosition,
    };
}

export function mapFilesByBulkUploadKey(files: File[]): {
    invalidFileNames: string[];
    duplicateMatches: string[];
    filesByKey: Map<string, { file: File; metadata: BulkUploadFileMetadata }>;
} {
    const filesByKey = new Map<string, { file: File; metadata: BulkUploadFileMetadata }>();
    const invalidFileNames: string[] = [];
    const duplicateMatches: string[] = [];

    files.forEach((file) => {
        const metadata = parseBulkUploadFileMetadataFromName(file.name);

        if (!metadata) {
            invalidFileNames.push(getFileNameWithoutExtension(file.name));
            return;
        }

        const normalizedKey = buildNormalizedBulkUploadKey(
            metadata.carrierNumber,
            metadata.borderoNumber,
            metadata.borderoPosition,
        );

        if (!normalizedKey) {
            return;
        }

        if (filesByKey.has(normalizedKey)) {
            duplicateMatches.push(getFileNameWithoutExtension(file.name));
            return;
        }

        filesByKey.set(normalizedKey, { file, metadata });
    });

    return { invalidFileNames, duplicateMatches, filesByKey };
}

export function toBase64Payload(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            const result = typeof reader.result === "string" ? reader.result : "";
            const separatorIndex = result.indexOf("base64,");
            resolve(separatorIndex >= 0 ? result.substring(separatorIndex + 7) : result);
        };

        reader.onerror = () => {
            reject(new Error("Unable to read file"));
        };

        reader.readAsDataURL(file);
    });
}

const UploadCellRenderer: React.FC<UploadCellRendererProps> = ({
    data,
    onFileSelected,
    uploadLabel,
    uploadingRowKey,
}) => {
    if (!data) {
        return null;
    }

    const rowKey = createRowKey(data);

    return (
        <Upload
            showUploadList={false}
            beforeUpload={(file) => {
                onFileSelected(file as File, data).catch(() => undefined);
                return false;
            }}
        >
            <Button
                icon={<UploadOutlined />}
                loading={uploadingRowKey === rowKey}
                disabled={Boolean(uploadingRowKey && uploadingRowKey !== rowKey)}
            >
                {uploadLabel}
            </Button>
        </Upload>
    );
};

export function getFreightStatusLabel(status: string | undefined, intl: IntlShape): string {
    switch (status) {
        case Types.BorderoEdpStatus.DocumentRequested:
            return getI18n("freight-status-document-requested", "Document Requested", intl);
        case Types.BorderoEdpStatus.DocumentReceived:
            return getI18n("freight-status-document-received", "Document Received", intl);
        case Types.BorderoEdpStatus.InProgress:
            return getI18n("freight-status-in-progress", "In Progress", intl);
        case Types.BorderoEdpStatus.InCorrection:
            return getI18n("freight-status-in-correction", "In Correction", intl);
        case Types.BorderoEdpStatus.InSettlement:
            return getI18n("freight-status-in-settlement", "In Settlement", intl);
        case Types.BorderoEdpStatus.Billed:
            return getI18n("freight-status-billed", "Billed", intl);
        case Types.BorderoEdpStatus.Ok:
            return getI18n("freight-status-ok", "OK", intl);
        default:
            return status || "";
    }
}

export function buildColumnDefs(
    intl: IntlShape,
    handleDocumentUpload: (file: File, row: BeleganforderungRow) => Promise<void>,
    uploadingRowKey: string | null,
): ColDef<BeleganforderungRow>[] {
    return [
        {
            headerName: "",
            colId: "select",
            checkboxSelection: true,
            headerCheckboxSelection: true,
            width: 50,
            pinned: "left",
            suppressMenu: true,
            sortable: false,
            resizable: false,
            filter: false,
        },
        {
            headerName: getI18n("freight-beleganforderung-grid-upload", "Upload", intl),
            colId: "upload",
            minWidth: 170,
            sortable: false,
            filter: false,
            resizable: false,
            cellRenderer: UploadCellRenderer,
            cellRendererParams: {
                onFileSelected: handleDocumentUpload,
                uploadLabel: getI18n("freight-beleganforderung-upload-button", "Upload", intl),
                uploadingRowKey,
            },
        },
        {headerName: getI18n("freight-beleganforderung-grid-carrier-number", "Carrier Number", intl), colId: "carrierNumber", field: "carrierNumber", minWidth: 150},
        {headerName: getI18n("freight-beleganforderung-grid-bordero-number", "Bordero Number", intl), colId: "borderoNumber", field: "borderoNumber", minWidth: 160},
        {headerName: getI18n("freight-beleganforderung-grid-bordero-date", "Bordero Date", intl), colId: "borderoDate", field: "borderoDate", minWidth: 140},
        {
            headerName: getI18n("freight-beleganforderung-grid-bordero-position", "Bordero Position", intl),
            colId: "borderoPosition",
            field: "borderoPosition",
            minWidth: 190,
        },
        {headerName: getI18n("freight-beleganforderung-grid-order-area", "Order Area", intl), colId: "orderArea", field: "orderArea", minWidth: 130},
        {headerName: getI18n("freight-beleganforderung-grid-relation", "Relation", intl), colId: "relation", field: "relation", minWidth: 120},
        {headerName: getI18n("freight-beleganforderung-grid-goods-category", "Goods Category", intl), colId: "goodsCategory", field: "goodsCategory", minWidth: 160},
        {headerName: getI18n("freight-beleganforderung-grid-load-number", "Load Number", intl), colId: "loadNumber", field: "loadNumber", minWidth: 140},
        {headerName: getI18n("freight-beleganforderung-grid-consignee", "Consignee", intl), colId: "consignee", field: "consignee", minWidth: 170},
        {headerName: getI18n("freight-beleganforderung-grid-consignor", "Consignor", intl), colId: "consignor", field: "consignor", minWidth: 170},
        {headerName: getI18n("freight-beleganforderung-grid-volume", "Volume", intl), colId: "volume", field: "volume", minWidth: 120},
        {headerName: getI18n("freight-beleganforderung-grid-weight", "Weight", intl), colId: "weight", field: "weight", minWidth: 110},
        {
            headerName: getI18n("freight-beleganforderung-grid-status", "Status", intl),
            colId: "status",
            field: "status",
            minWidth: 150,
            valueFormatter: (params) => getFreightStatusLabel(typeof params.value === "string" ? params.value : undefined, intl),
        },
    ];
}

export function buildMenuItems(
    intl: IntlShape,
    menuVersion: number,
    hasPersonalizedView: boolean,
    onApplyPersonal: () => void,
    onSave: () => void,
): MenuProps["items"] {
    return [
        {
            key: `3-${menuVersion}`,
            disabled: !hasPersonalizedView,
            icon: <UserOutlined />,
            onClick: onApplyPersonal,
            label: (
                <span data-testid="info-portal-personal">
                    {intl.formatMessage({id: "info-portal-personal", defaultMessage: "Personal"})}
                </span>
            ),
        },
        {
            type: "divider",
        },
        {
            key: `4-${menuVersion}`,
            icon: <SaveOutlined />,
            onClick: onSave,
            label: (
                <span data-testid="info-portal-save">
                    {intl.formatMessage({id: "info-portal-save", defaultMessage: "Save"})}
                </span>
            ),
        },
    ];
}

function ensureSuccessfulUploadStatus(response: any): void {
    const statusCode = response.data?.sendUploadedDocumentsToFavorCockpit;
    if (!statusCode || statusCode < 200 || statusCode >= 300) {
        throw new Error(`Upload service returned status ${statusCode || 500}`);
    }
}

export async function uploadDocumentForRow(
    file: File,
    row: BeleganforderungRow,
    sendUploadedDocumentsToFavorCockpitMutation: (options: { context: { skip401Logout: boolean }, variables: { request: Record<string, string> } }) => Promise<any>,
): Promise<void> {
    const base64FileData = await toBase64Payload(file);

    // Format borderoDate as yyyy-MM-dd if it is in yyyyMMdd format
    let formattedBorderoDate = row.borderoDate;
    if (/^\d{8}$/.test(row.borderoDate)) {
        formattedBorderoDate = `${row.borderoDate.substring(0, 4)}-${row.borderoDate.substring(4, 6)}-${row.borderoDate.substring(6, 8)}`;
    }

    const response = await sendUploadedDocumentsToFavorCockpitMutation({
        context: {
            skip401Logout: true,
        },
        variables: {
            request: {
                carrier: row.carrierNumber,
                borderoNumber: row.borderoNumber,
                borderoDate: formattedBorderoDate,
                borderoPositionNumber: row.borderoPosition,
                document: base64FileData,
                filename: file.name,
                contentType: file.type || "application/octet-stream",
            },
        },
    });

    ensureSuccessfulUploadStatus(response);
}

export function notifyUploadFailure(intl: IntlShape, error: unknown, borderoNumber: string): void {
    if (isUnauthorizedError(error)) {
        notification.error({
            message: getI18n("freight-beleganforderung-upload-unauthorized", "You are not authorized to upload documents.", intl),
            description: borderoNumber,
        });
        return;
    }

    notification.error({
        message: getI18n("freight-beleganforderung-upload-error", "Document upload failed.", intl),
        description: error instanceof Error ? error.message : borderoNumber,
    });
}

export function showNoSelectionWarning(intl: IntlShape, title = "Export"): void {
    notification.warning({
        message: getI18n("Warning-message", title, intl),
        description: getI18n(
            "Warning-export-message",
            "You have not selected any items to be exported. Please select an item first.",
            intl,
        ),
    });
}

export function notifyBulkUploadSummary(
    intl: IntlShape,
    borderoSummary: Array<{ borderoNumber: string; successCount: number; failedCount: number }>,
    invalidFileNames: string[],
    duplicateMatches: string[],
): void {
    const totalSuccess = borderoSummary.reduce((acc, item) => acc + item.successCount, 0);
    const totalFailedUploads = borderoSummary.reduce((acc, item) => acc + item.failedCount, 0);
    const totalFailed = totalFailedUploads + invalidFileNames.length + duplicateMatches.length;
    const hasAnyProblem = totalFailed > 0;

    let message = getI18n("freight-beleganforderung-upload-summary-title-success", "Upload successful:", intl);
    if (totalSuccess === 0 && totalFailed > 0) {
        message = getI18n("freight-beleganforderung-upload-summary-title-failed", "Upload not successful:", intl);
    } else if (totalSuccess > 0 && totalFailed > 0) {
        message = getI18n("freight-beleganforderung-upload-summary-title-warning", "Upload successful with errors:", intl);
    }
    const description = [
        `${getI18n("freight-beleganforderung-upload-summary-total-success", "Total successful uploads", intl)}: ${totalSuccess}`,
        `${getI18n("freight-beleganforderung-upload-summary-total-failed", "Total failed uploads", intl)}: ${totalFailed}`,
    ].join("\n");

    if (hasAnyProblem) {
        notification.warning({
            message,
            description,
            duration: 8,
        });
        return;
    }

    notification.success({
        message,
        description,
        duration: 8,
    });
}

export function exportSelectedRows(gridApi: any, intl: IntlShape): void {
    const columnIDs = gridApi
        ?.getColumnDefs()
        ?.filter((item: any) => !item["hide"])
        ?.map((item: any) => item["colId"]) ?? [];
    const selectedRows = gridApi?.getSelectedRows() ?? [];

    if (selectedRows.length && columnIDs.length) {
        gridApi.exportDataAsExcel({
            onlySelectedAllPages: true,
            columnKeys: columnIDs,
        });
        return;
    }

    showNoSelectionWarning(intl);
}